import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BookOpen, Pencil, Plus, Trash2, Check, X, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { getAdminHeaders, canEdit } from '../lib/auth';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VerseCard = ({ isAdmin = false }) => {
  const [verse, setVerse] = useState(null);
  const [showManager, setShowManager] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchCurrent = useCallback(async () => {
    try {
      const headers = isAdmin ? getAdminHeaders() : {};
      const res = await axios.get(`${API}/verses/current`, { headers });
      setVerse(res.data.verse);
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { fetchCurrent(); }, [fetchCurrent]);

  if (loading) return null;

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch { return d; }
  };

  return (
    <>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200/60 shadow-sm overflow-hidden" data-testid="verse-card">
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-amber-700" />
              </div>
              <span className="font-nunito font-bold text-sm text-amber-900">Versículo da Semana</span>
            </div>
            {isAdmin && (
              <button
                onClick={() => setShowManager(true)}
                className="text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-full p-1.5 transition-all"
                data-testid="verse-manage-btn"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>
          {verse ? (
            <div>
              <p className="font-serif text-base text-amber-950 leading-relaxed italic">
                "{verse.texto}"
              </p>
              <p className="font-inter text-sm font-bold text-amber-700 mt-2">
                {verse.referencia}
              </p>
              {verse.data_inicio && (
                <p className="font-inter text-[10px] text-amber-500 mt-1">
                  {formatDate(verse.data_inicio)} a {formatDate(verse.data_fim)}
                </p>
              )}
            </div>
          ) : (
            <p className="font-inter text-sm text-amber-500 italic">Nenhum versículo definido para esta semana</p>
          )}
        </div>
      </div>

      {showManager && (
        <VerseManager
          onClose={() => { setShowManager(false); fetchCurrent(); }}
        />
      )}
    </>
  );
};

const VerseManager = ({ onClose }) => {
  const [verses, setVerses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ texto: '', referencia: '', data_inicio: '', data_fim: '' });
  const [showHistory, setShowHistory] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/verses`, { headers: getAdminHeaders() });
      setVerses(res.data);
    } catch { toast.error('Erro ao carregar versiculos'); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const getSundayOfWeek = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    return d.toISOString().split('T')[0];
  };

  const getSaturdayOfWeek = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    const day = d.getDay();
    d.setDate(d.getDate() + (6 - day));
    return d.toISOString().split('T')[0];
  };

  const handleDateChange = (field, value) => {
    if (field === 'data_inicio') {
      const sunday = getSundayOfWeek(value);
      const saturday = getSaturdayOfWeek(value);
      setForm(p => ({ ...p, data_inicio: sunday, data_fim: saturday }));
    } else {
      setForm(p => ({ ...p, [field]: value }));
    }
  };

  const resetForm = () => {
    setForm({ texto: '', referencia: '', data_inicio: '', data_fim: '' });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.texto || !form.referencia) {
      toast.error('Preencha texto e referencia'); return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/verses/${editing}`, form, { headers: getAdminHeaders() });
        toast.success('Versículo atualizado!');
      } else {
        await axios.post(`${API}/verses`, form, { headers: getAdminHeaders() });
        toast.success('Versículo criado!');
      }
      resetForm();
      fetchAll();
    } catch { toast.error('Erro ao salvar'); }
  };

  const handleActivate = async (id) => {
    try {
      await axios.put(`${API}/verses/${id}/activate`, {}, { headers: getAdminHeaders() });
      toast.success('Versículo ativado como da semana!');
      fetchAll();
    } catch { toast.error('Erro ao ativar'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/verses/${id}`, { headers: getAdminHeaders() });
      toast.success('Removido!');
      fetchAll();
    } catch { toast.error('Erro ao remover'); }
  };

  const startEdit = (v) => {
    setForm({ texto: v.texto, referencia: v.referencia, data_inicio: v.data_inicio || '', data_fim: v.data_fim || '' });
    setEditing(v.id);
    setShowForm(true);
  };

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }); }
    catch { return d; }
  };

  const activeVerse = verses.find(v => v.ativo);
  const historyVerses = verses.filter(v => !v.ativo);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="verse-manager">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-amber-600" />
            <h2 className="font-nunito font-bold text-lg">Versículos da Semana</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-testid="verse-manager-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Active verse */}
          {activeVerse && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="active-verse">
              <div className="flex items-center gap-1.5 mb-2">
                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                <span className="text-xs font-bold text-amber-700">ATIVO</span>
              </div>
              <p className="text-sm italic text-amber-950">"{activeVerse.texto}"</p>
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs font-bold text-amber-700">{activeVerse.referencia}</p>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(activeVerse)} className="text-amber-600 hover:bg-amber-100 rounded p-1" data-testid="edit-active-verse">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {activeVerse.data_inicio && (
                <p className="text-[10px] text-amber-500 mt-1">{formatDate(activeVerse.data_inicio)} a {formatDate(activeVerse.data_fim)}</p>
              )}
            </div>
          )}

          {/* Add/Edit form */}
          {showForm ? (
            <div className="bg-gray-50 rounded-xl p-4 space-y-3" data-testid="verse-form">
              <textarea
                value={form.texto}
                onChange={e => setForm(p => ({ ...p, texto: e.target.value }))}
                placeholder="Texto do versículo..."
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none h-20 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                data-testid="verse-input-texto"
              />
              <input
                value={form.referencia}
                onChange={e => setForm(p => ({ ...p, referencia: e.target.value }))}
                placeholder="Referencia (ex: Joao 3:16)"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                data-testid="verse-input-referencia"
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Inicio (domingo)</label>
                  <input
                    type="date"
                    value={form.data_inicio}
                    onChange={e => handleDateChange('data_inicio', e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                    data-testid="verse-input-inicio"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Fim (sabado)</label>
                  <input
                    type="date"
                    value={form.data_fim}
                    onChange={e => setForm(p => ({ ...p, data_fim: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-100"
                    readOnly
                    data-testid="verse-input-fim"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  className="flex-1 bg-[#1A4D2E] text-white py-2 rounded-lg text-sm font-semibold hover:bg-[#145A32] transition-colors flex items-center justify-center gap-1.5"
                  data-testid="verse-save-btn"
                >
                  <Check className="w-4 h-4" />
                  {editing ? 'Atualizar' : 'Salvar'}
                </button>
                <button
                  onClick={resetForm}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50 transition-colors"
                  data-testid="verse-cancel-btn"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-gray-500 hover:border-amber-400 hover:text-amber-600 transition-colors text-sm font-medium"
              data-testid="verse-add-btn"
            >
              <Plus className="w-4 h-4" /> Novo Versículo
            </button>
          )}

          {/* History */}
          {historyVerses.length > 0 && (
            <div>
              <button
                onClick={() => setShowHistory(h => !h)}
                className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors mb-2"
                data-testid="verse-history-toggle"
              >
                {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Historico ({historyVerses.length})
              </button>
              {showHistory && (
                <div className="space-y-2">
                  {historyVerses.map(v => (
                    <div key={v.id} className="bg-gray-50 rounded-xl p-3 border border-gray-100" data-testid={`verse-history-${v.id}`}>
                      <p className="text-sm italic text-gray-700">"{v.texto}"</p>
                      <div className="flex items-center justify-between mt-2">
                        <div>
                          <p className="text-xs font-bold text-gray-600">{v.referencia}</p>
                          {v.data_inicio && (
                            <p className="text-[10px] text-gray-400">{formatDate(v.data_inicio)} a {formatDate(v.data_fim)}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => handleActivate(v.id)} className="text-green-600 hover:bg-green-50 rounded p-1 text-xs flex items-center gap-0.5" data-testid={`verse-activate-${v.id}`} title="Ativar">
                            <Star className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => startEdit(v)} className="text-gray-400 hover:bg-gray-100 rounded p-1" data-testid={`verse-edit-${v.id}`} title="Editar">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDelete(v.id)} className="text-red-400 hover:bg-red-50 rounded p-1" data-testid={`verse-delete-${v.id}`} title="Remover">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
